import { FC, useMemo } from 'react';
import { Tooltip, TooltipProps, Typography, TypographyProps, Card, CardContent } from '@mui/material';
import { HelpOutlineOutlined as HelpOutlineOutlinedIcon } from '@mui/icons-material';
import moment from 'moment-timezone';
import { AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon } from '@mui/icons-material';
import Column from '../../../../../components/Column';
import Row from '../../../../../components/Row';

import { Campaign, CampaignFlight } from '../../../../../types/Campaign';
import { useUserContext } from '../../../../../hooks/Contexts/UserContext';
import Utils from '../../../../../components/Utils';

interface TooltipTypographyProps extends TypographyProps {
    tooltip?: TooltipProps['title'];
}

const CampaignSummaryTitle: FC<TooltipTypographyProps> = ({ children, tooltip, ...props }) => {
    return (
        <Row alignItems="center" gap={0.5}>
            <Typography variant="caption" color="text.primary" {...props}>
                {children}
            </Typography>

            {tooltip && (
                <Tooltip title={tooltip}>
                    <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                </Tooltip>
            )}
        </Row>
    );
};

const CampaignSummarySubtitle: FC<TooltipTypographyProps> = ({ children, tooltip, ...props }) => {
    return (
        <Row alignItems="center" gap={0.5}>
            <Typography variant="subtitle2" fontWeight={500} {...props}>
                {children}
            </Typography>

            {tooltip && (
                <Tooltip title={tooltip}>
                    <HelpOutlineOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                </Tooltip>
            )}
        </Row>
    );
};

const AdminOnlyIcon = () => {
    return (
        <Tooltip title="Visible to Admins Only">
            <AdminPanelSettingsOutlinedIcon
                color="primary"
                sx={{ fontSize: 17, ml: 1, verticalAlign: 'text-bottom' }}
            />
        </Tooltip>
    );
};

// Helper functions
const getCurrentMonthYear = () => {
    const now = moment.tz('America/New_York').toDate();
    return {
        currentMonth: now.getMonth(),
        currentYear: now.getFullYear(),
    };
};

const getFlightsForCurrentMonth = (flights: CampaignFlight[]) => {
    const { currentMonth, currentYear } = getCurrentMonthYear();

    return flights.filter((flight) => {
        const endDate = new Date(Utils.formatESTDate(flight.endDateTime));
        return endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear;
    });
};

const calculateTotalBudget = (
    flights: CampaignFlight[],
    field: keyof Pick<CampaignFlight, 'budgetAmount' | 'dspBudget'>
) => {
    return flights.reduce((sum, flight) => sum + flight[field], 0);
};

const getLatestEndDate = (flights: CampaignFlight[]): Date | null => {
    if (flights.length === 0) return null;

    const timestamps = flights.map((flight) => new Date(flight.endDateTime).getTime());
    return new Date(Math.max(...timestamps));
};

interface CampaignSummaryCardProps {
    campaign: Campaign;
}

const CampaignSummaryCard: FC<CampaignSummaryCardProps> = ({ campaign }) => {
    const { userContext } = useUserContext();

    const currentMonthBudget = useMemo(
        () => calculateTotalBudget(campaign.flights, 'budgetAmount'),
        [campaign.flights]
    );

    const currentMonthDspBudget = useMemo(
        () => calculateTotalBudget(campaign.flights, 'dspBudget'),
        [campaign.flights]
    );

    const currentMonthEndDate = useMemo(() => {
        const flightsForMonth = getFlightsForCurrentMonth(campaign.flights);
        return getLatestEndDate(flightsForMonth);
    }, [campaign.flights]);

    return (
        <Card variant="outlined">
            <CardContent>
                <Row gap={3}>
                    <Column gap={1}>
                        <CampaignSummaryTitle>Budget</CampaignSummaryTitle>
                        <CampaignSummarySubtitle>
                            {currentMonthBudget > 0 ? Utils.formatCurrency(currentMonthBudget) : ' - '}
                        </CampaignSummarySubtitle>
                    </Column>

                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                        <Column gap={1}>
                            <CampaignSummaryTitle>
                                DSP Budget
                                <AdminOnlyIcon />
                            </CampaignSummaryTitle>
                            <CampaignSummarySubtitle>
                                {currentMonthDspBudget > 0 ? Utils.formatCurrency(currentMonthDspBudget) : ' - '}
                            </CampaignSummarySubtitle>
                        </Column>
                    )}

                    <Column gap={1}>
                        <CampaignSummaryTitle tooltip="This is the start date of the first flight.">
                            Start Date
                        </CampaignSummaryTitle>
                        <CampaignSummarySubtitle>
                            {campaign.startDate ? Utils.formatESTDate(campaign.startDate) : ' - '}
                        </CampaignSummarySubtitle>
                    </Column>

                    <Column gap={1}>
                        <CampaignSummaryTitle tooltip="This is the end date of the last flight.">
                            End Date
                        </CampaignSummaryTitle>
                        <CampaignSummarySubtitle>
                            {currentMonthEndDate ? Utils.formatESTDate(campaign.endDate) : ' - '}
                        </CampaignSummarySubtitle>
                    </Column>

                    <Column gap={1}>
                        <CampaignSummaryTitle>Flights</CampaignSummaryTitle>
                        <CampaignSummarySubtitle>{campaign.flights.length ?? ' - '}</CampaignSummarySubtitle>
                    </Column>

                    <Column gap={1}>
                        <CampaignSummaryTitle tooltip="Total impression goal across all flights.">
                            Impression Goal
                        </CampaignSummaryTitle>
                        <CampaignSummarySubtitle>
                            {(() => {
                                const totalGoal = campaign.flights.reduce((sum, f) => sum + (f.impressionGoal ?? 0), 0);
                                return totalGoal > 0 ? Number(totalGoal).toLocaleString() : ' - ';
                            })()}
                        </CampaignSummarySubtitle>
                    </Column>

                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                        <Column gap={1}>
                            <CampaignSummaryTitle>
                                Ad Groups
                                <AdminOnlyIcon />
                            </CampaignSummaryTitle>
                            <CampaignSummarySubtitle>{campaign.adGroups.length ?? ' - '}</CampaignSummarySubtitle>
                        </Column>
                    )}

                    {(userContext.isAdmin() || userContext.isOpsAdmin()) && (
                        <Column gap={1}>
                            <CampaignSummaryTitle>
                                Version
                                <AdminOnlyIcon />
                            </CampaignSummaryTitle>
                            <CampaignSummarySubtitle>
                                {campaign.version ? `v${campaign.version}` : ' - '}
                            </CampaignSummarySubtitle>
                        </Column>
                    )}
                </Row>
            </CardContent>
        </Card>
    );
};

export type { CampaignSummaryCardProps };
export default CampaignSummaryCard;
